'use client';

import React, { useState, useEffect } from 'react';
import { toast } from 'sonner';
import { enseignesService } from '@/lib/supabase/services';
import type { Enseigne } from '@/lib/supabase/services';

const SECTEURS = ['Mode & Textile', 'Restauration rapide', 'Café & Restauration', 'Décoration & Maison', 'Santé & Beauté', 'Grande distribution', 'Sport & Loisirs', 'Beauté & Cosmétiques', 'High-tech', 'Banque & Finance', 'Autre'];
const STATUTS_ENSEIGNE = ['Prospect', 'Actif', 'En recherche', 'En visite', 'En négociation', 'Inactif'];
const VILLES = ['Casablanca', 'Rabat', 'Marrakech', 'Fès', 'Tanger', 'Agadir', 'Meknès', 'Oujda'];

const statusColors: Record<string, string> = {
  'Actif': 'bg-emerald-50 text-emerald-700 border-emerald-200',
  'En recherche': 'bg-blue-50 text-blue-700 border-blue-200',
  'En visite': 'bg-cyan-50 text-cyan-700 border-cyan-200',
  'En négociation': 'bg-amber-50 text-amber-700 border-amber-200',
  'Prospect': 'bg-violet-50 text-violet-700 border-violet-200',
  'Inactif': 'bg-slate-50 text-slate-500 border-slate-200',
};

interface EnseigneFormData {
  nom: string; raisonSociale: string; secteur: string; statut: string; contact: string;
  villesCibles: string[]; surfaceMin: string; surfaceMax: string; surfaceRDCMin: string;
  facadeMin: string; budgetMax: string; droitEntreeMax: string; travauxPrisEnCharge: boolean;
  extraction: boolean; hauteurMin: string; puissanceMin: string; parking: boolean;
  livraison: boolean; accessibilite: boolean; terrasse: boolean;
  urgence: string; horizonOuverture: string; nbOuverturesPrevues: string;
  centresCommerciaux: string; quartiersExclus: string; notes: string;
}

const defaultEnseigneForm: EnseigneFormData = {
  nom: '', raisonSociale: '', secteur: '', statut: 'Prospect', contact: '',
  villesCibles: [], surfaceMin: '', surfaceMax: '', surfaceRDCMin: '', facadeMin: '',
  budgetMax: '', droitEntreeMax: '', travauxPrisEnCharge: false,
  extraction: false, hauteurMin: '', puissanceMin: '', parking: false,
  livraison: false, accessibilite: false, terrasse: false,
  urgence: 'Normale', horizonOuverture: '', nbOuverturesPrevues: '',
  centresCommerciaux: '', quartiersExclus: '', notes: '',
};

export default function EnseignesContent() {
  const [enseignes, setEnseignes] = useState<Enseigne[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filterStatut, setFilterStatut] = useState('');
  const [filterSecteur, setFilterSecteur] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [form, setForm] = useState<EnseigneFormData>(defaultEnseigneForm);
  const [activeFormTab, setActiveFormTab] = useState('identite');
  const [deleteConfirmId, setDeleteConfirmId] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    enseignesService.getAll().then(data => {
      setEnseignes(data);
      setLoading(false);
    });
  }, []);

  const filtered = enseignes.filter(e => {
    if (search && !e.nom.toLowerCase().includes(search.toLowerCase()) && !e.secteur.toLowerCase().includes(search.toLowerCase())) return false;
    if (filterStatut && e.statut !== filterStatut) return false;
    if (filterSecteur && e.secteur !== filterSecteur) return false;
    return true;
  });

  const setField = (key: keyof EnseigneFormData, value: string | boolean | string[]) => {
    setForm(f => ({ ...f, [key]: value }));
  };

  const toggleVille = (ville: string) => {
    setForm(f => ({
      ...f,
      villesCibles: f.villesCibles.includes(ville)
        ? f.villesCibles.filter(v => v !== ville)
        : [...f.villesCibles, ville],
    }));
  };

  const openCreate = () => {
    setForm(defaultEnseigneForm);
    setEditingId(null);
    setActiveFormTab('identite');
    setShowModal(true);
  };

  const openEdit = (e: Enseigne) => {
    setForm({
      nom: e.nom,
      raisonSociale: e.raisonSociale || '',
      secteur: e.secteur || '',
      statut: e.statut || 'Prospect',
      contact: e.contact || '',
      villesCibles: e.villesCibles || [],
      surfaceMin: String(e.surfaceMin || ''),
      surfaceMax: String(e.surfaceMax || ''),
      surfaceRDCMin: '',
      facadeMin: String(e.facade || ''),
      budgetMax: String(e.budgetMax || ''),
      droitEntreeMax: '',
      travauxPrisEnCharge: false,
      extraction: false,
      hauteurMin: '',
      puissanceMin: '',
      parking: false,
      livraison: false,
      accessibilite: false,
      terrasse: false,
      urgence: 'Normale',
      horizonOuverture: '',
      nbOuverturesPrevues: '',
      centresCommerciaux: '',
      quartiersExclus: '',
      notes: '',
    });
    setEditingId(e.id);
    setActiveFormTab('identite');
    setShowModal(true);
  };

  const handleSave = async () => {
    if (!form.nom.trim()) {
      toast.error('Le nom commercial est obligatoire');
      return;
    }
    setSaving(true);
    if (editingId) {
      const updated = await enseignesService.update(editingId, form);
      if (updated) {
        setEnseignes(prev => prev.map(e => e.id === editingId ? updated : e));
        toast.success('Enseigne mise à jour !');
      } else {
        toast.error('Erreur lors de la mise à jour');
      }
    } else {
      const created = await enseignesService.create(form);
      if (created) {
        setEnseignes(prev => [created, ...prev]);
        toast.success('Enseigne créée avec succès !');
      } else {
        toast.error('Erreur lors de la création');
      }
    }
    setSaving(false);
    setShowModal(false);
  };

  const handleDelete = async (id: string) => {
    const enseigne = enseignes.find(e => e.id === id);
    const ok = await enseignesService.delete(id);
    if (ok) {
      setEnseignes(prev => prev.filter(e => e.id !== id));
      toast.success(`Enseigne "${enseigne?.nom}" supprimée`);
    } else {
      toast.error('Erreur lors de la suppression');
    }
    setDeleteConfirmId(null);
  };

  const inputCls = 'input-field';
  const labelCls = 'block text-xs font-600 text-slate-600 mb-1.5 uppercase tracking-wide';
  const FORM_TABS = [
    { id: 'identite', label: 'Identité' },
    { id: 'implantation', label: 'Implantation' },
    { id: 'surfaces', label: 'Surfaces & Budget' },
    { id: 'technique', label: 'Technique' },
    { id: 'timing', label: 'Timing' },
  ];

  const enseigneToDelete = enseignes.find(e => e.id === deleteConfirmId);

  return (
    <div className="px-6 lg:px-8 xl:px-10 py-7 max-w-screen-2xl mx-auto">

      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl font-700 text-slate-900">Enseignes & Locataires</h1>
          <p className="text-sm text-slate-500 mt-0.5">{loading ? 'Chargement...' : `${filtered.length} enseigne${filtered.length !== 1 ? 's' : ''} · Cahiers des charges d'implantation`}</p>
        </div>
        <button onClick={openCreate} className="btn-primary">
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
          </svg>
          Nouvelle enseigne
        </button>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-xl border border-slate-200 p-4 shadow-sm mb-5 flex flex-wrap gap-3">
        <div className="flex-1 min-w-48 relative">
          <svg className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
          </svg>
          <input type="text" className={`${inputCls} pl-9`} placeholder="Rechercher une enseigne..." value={search} onChange={e => setSearch(e.target.value)} />
        </div>
        <select className={`${inputCls} w-auto min-w-36`} value={filterStatut} onChange={e => setFilterStatut(e.target.value)}>
          <option value="">Tous les statuts</option>
          {STATUTS_ENSEIGNE.map(s => <option key={s}>{s}</option>)}
        </select>
        <select className={`${inputCls} w-auto min-w-40`} value={filterSecteur} onChange={e => setFilterSecteur(e.target.value)}>
          <option value="">Tous les secteurs</option>
          {SECTEURS.map(s => <option key={s}>{s}</option>)}
        </select>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-24">
          <svg className="w-8 h-8 animate-spin text-primary-700" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
          </svg>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {filtered.length === 0 && (
            <div className="col-span-full flex flex-col items-center justify-center py-16 text-center">
              <div className="w-14 h-14 bg-slate-100 rounded-2xl flex items-center justify-center mb-3">
                <svg className="w-7 h-7 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5" />
                </svg>
              </div>
              <p className="text-sm font-600 text-slate-700">Aucune enseigne trouvée</p>
              <p className="text-xs text-slate-400 mt-1">Modifiez vos filtres ou ajoutez une nouvelle enseigne</p>
              <button onClick={openCreate} className="mt-4 btn-primary text-sm">Nouvelle enseigne</button>
            </div>
          )}
          {filtered.map(e => (
            <div key={e.id} className="bg-white rounded-xl border border-slate-200 p-5 shadow-sm hover:shadow-card-hover transition-all duration-200 group">
              <div className="flex items-start justify-between gap-3 mb-4">
                <div className="flex items-center gap-3">
                  <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-navy-100 to-primary-100 flex items-center justify-center text-primary-700 font-700 text-base flex-shrink-0">
                    {e.nom.charAt(0)}
                  </div>
                  <div>
                    <p className="font-700 text-slate-900 text-sm">{e.nom}</p>
                    <p className="text-xs text-slate-400 truncate max-w-32">{e.raisonSociale}</p>
                  </div>
                </div>
                <span className={`status-badge border text-xs flex-shrink-0 ${statusColors[e.statut] || 'bg-slate-50 text-slate-600 border-slate-200'}`}>
                  {e.statut}
                </span>
              </div>
              <div className="space-y-2 mb-4">
                <div className="flex items-center gap-2">
                  <span className="text-xs px-2 py-0.5 bg-slate-100 text-slate-600 rounded-full">{e.secteur}</span>
                </div>
                <div className="grid grid-cols-2 gap-2 text-xs">
                  <div className="bg-slate-50 rounded-lg p-2">
                    <p className="text-slate-400">Surface</p>
                    <p className="font-600 text-slate-800">{e.surfaceMin}–{e.surfaceMax} m²</p>
                  </div>
                  <div className="bg-slate-50 rounded-lg p-2">
                    <p className="text-slate-400">Budget max</p>
                    <p className="font-600 text-slate-800">{e.budgetMax.toLocaleString('fr-FR')} MAD</p>
                  </div>
                </div>
                <div className="flex flex-wrap gap-1">
                  {e.villesCibles?.slice(0, 3).map(v => (
                    <span key={v} className="text-xs px-2 py-0.5 bg-primary-50 text-primary-700 rounded-full">{v}</span>
                  ))}
                  {(e.villesCibles?.length || 0) > 3 && (
                    <span className="text-xs px-2 py-0.5 bg-slate-100 text-slate-500 rounded-full">+{e.villesCibles.length - 3}</span>
                  )}
                </div>
              </div>
              <div className="flex items-center justify-between pt-3 border-t border-slate-100">
                <div className="flex items-center gap-3 text-xs text-slate-400">
                  <span className="flex items-center gap-1">
                    <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5" />
                    </svg>
                    {e.nbLocaux} local{e.nbLocaux !== 1 ? 'aux' : ''}
                  </span>
                  <span className={`font-700 ${e.score >= 90 ? 'text-emerald-600' : e.score >= 80 ? 'text-primary-600' : 'text-amber-600'}`}>
                    Score: {e.score}
                  </span>
                </div>
                <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                  <button onClick={() => openEdit(e)} className="p-1.5 rounded-lg hover:bg-slate-100 transition-colors text-slate-400 hover:text-slate-700" title="Modifier">
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" /></svg>
                  </button>
                  <button onClick={() => window.location.href = '/matching'} className="p-1.5 rounded-lg hover:bg-slate-100 transition-colors text-slate-400 hover:text-primary-700" title="Voir le matching">
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" /></svg>
                  </button>
                  <button onClick={() => setDeleteConfirmId(e.id)} className="p-1.5 rounded-lg hover:bg-red-50 transition-colors text-slate-400 hover:text-red-600" title="Supprimer">
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" /></svg>
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Delete Confirm Modal */}
      {deleteConfirmId && enseigneToDelete && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4 animate-fade-in">
          <div className="bg-white rounded-2xl shadow-modal border border-slate-200 p-6 max-w-md w-full">
            <div className="flex items-start gap-4">
              <div className="w-10 h-10 bg-red-100 rounded-xl flex items-center justify-center flex-shrink-0">
                <svg className="w-5 h-5 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" /></svg>
              </div>
              <div>
                <h3 className="text-base font-700 text-slate-900 mb-1">Supprimer cette enseigne ?</h3>
                <p className="text-sm text-slate-500 leading-relaxed">L'enseigne <span className="font-600 text-slate-700">"{enseigneToDelete.nom}"</span> sera définitivement supprimée.</p>
              </div>
            </div>
            <div className="flex items-center justify-end gap-3 mt-6">
              <button onClick={() => setDeleteConfirmId(null)} className="px-4 py-2 text-sm font-600 text-slate-700 bg-slate-100 rounded-lg hover:bg-slate-200 transition-colors">Annuler</button>
              <button onClick={() => handleDelete(deleteConfirmId)} className="px-4 py-2 text-sm font-600 text-white bg-red-600 rounded-lg hover:bg-red-700 active:scale-95 transition-all">Supprimer</button>
            </div>
          </div>
        </div>
      )}

      {/* Create/Edit Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4 animate-fade-in">
          <div className="bg-white rounded-2xl shadow-modal w-full max-w-2xl max-h-[90vh] flex flex-col">
            {/* Modal header */}
            <div className="flex items-center justify-between p-6 border-b border-slate-200">
              <h2 className="text-lg font-700 text-slate-900">{editingId ? 'Modifier l\'enseigne' : 'Nouvelle enseigne'}</h2>
              <button onClick={() => setShowModal(false)} className="p-2 rounded-lg hover:bg-slate-100 transition-colors text-slate-400">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
              </button>
            </div>

            {/* Form tabs */}
            <div className="flex items-center gap-1 px-6 pt-4 border-b border-slate-100 overflow-x-auto">
              {FORM_TABS.map(tab => (
                <button
                  key={tab.id}
                  onClick={() => setActiveFormTab(tab.id)}
                  className={`px-3 py-2 text-xs font-600 rounded-t-lg whitespace-nowrap transition-all ${
                    activeFormTab === tab.id ? 'text-primary-700 border-b-2 border-primary-700' : 'text-slate-500 hover:text-slate-700'
                  }`}
                >
                  {tab.label}
                </button>
              ))}
            </div>

            {/* Form body */}
            <div className="flex-1 overflow-y-auto p-6 scrollbar-thin">
              {activeFormTab === 'identite' && (
                <div className="space-y-4">
                  <div><label className={labelCls}>Nom commercial <span className="text-red-500">*</span></label><input type="text" className={inputCls} placeholder="Ex: Zara Home" value={form.nom} onChange={e => setField('nom', e.target.value)} /></div>
                  <div><label className={labelCls}>Raison sociale</label><input type="text" className={inputCls} placeholder="Ex: Inditex Maroc SARL" value={form.raisonSociale} onChange={e => setField('raisonSociale', e.target.value)} /></div>
                  <div>
                    <label className={labelCls}>Secteur d'activité</label>
                    <select className={inputCls} value={form.secteur} onChange={e => setField('secteur', e.target.value)}>
                      <option value="">— Sélectionner —</option>
                      {SECTEURS.map(s => <option key={s}>{s}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className={labelCls}>Statut</label>
                    <select className={inputCls} value={form.statut} onChange={e => setField('statut', e.target.value)}>
                      {STATUTS_ENSEIGNE.map(s => <option key={s}>{s}</option>)}
                    </select>
                  </div>
                  <div><label className={labelCls}>Contact principal</label><input type="text" className={inputCls} placeholder="Nom, prénom, poste" value={form.contact} onChange={e => setField('contact', e.target.value)} /></div>
                  <div><label className={labelCls}>Notes</label><textarea className={inputCls} rows={3} placeholder="Informations complémentaires..." value={form.notes} onChange={e => setField('notes', e.target.value)} /></div>
                </div>
              )}

              {activeFormTab === 'implantation' && (
                <div className="space-y-4">
                  <div>
                    <label className={labelCls}>Villes cibles</label>
                    <div className="flex flex-wrap gap-2 mt-2">
                      {VILLES.map(v => (
                        <button
                          key={v}
                          type="button"
                          onClick={() => toggleVille(v)}
                          className={`px-3 py-1.5 rounded-full text-xs font-600 border transition-all ${
                            form.villesCibles.includes(v)
                              ? 'bg-primary-700 text-white border-primary-700' : 'bg-white text-slate-600 border-slate-200 hover:border-primary-300'
                          }`}
                        >
                          {v}
                        </button>
                      ))}
                    </div>
                  </div>
                  <div><label className={labelCls}>Centres commerciaux préférés</label><input type="text" className={inputCls} placeholder="Ex: Morocco Mall, Anfa Place..." value={form.centresCommerciaux} onChange={e => setField('centresCommerciaux', e.target.value)} /></div>
                  <div><label className={labelCls}>Quartiers exclus</label><input type="text" className={inputCls} placeholder="Zones à éviter..." value={form.quartiersExclus} onChange={e => setField('quartiersExclus', e.target.value)} /></div>
                </div>
              )}

              {activeFormTab === 'surfaces' && (
                <div className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div><label className={labelCls}>Surface min (m²)</label><input type="number" className={inputCls} placeholder="0" value={form.surfaceMin} onChange={e => setField('surfaceMin', e.target.value)} /></div>
                    <div><label className={labelCls}>Surface max (m²)</label><input type="number" className={inputCls} placeholder="0" value={form.surfaceMax} onChange={e => setField('surfaceMax', e.target.value)} /></div>
                    <div><label className={labelCls}>Surface RDC min (m²)</label><input type="number" className={inputCls} placeholder="0" value={form.surfaceRDCMin} onChange={e => setField('surfaceRDCMin', e.target.value)} /></div>
                    <div><label className={labelCls}>Façade min (m)</label><input type="number" className={inputCls} placeholder="0" value={form.facadeMin} onChange={e => setField('facadeMin', e.target.value)} /></div>
                    <div><label className={labelCls}>Budget loyer max (MAD)</label><input type="number" className={inputCls} placeholder="0" value={form.budgetMax} onChange={e => setField('budgetMax', e.target.value)} /></div>
                    <div><label className={labelCls}>Droit d'entrée max (MAD)</label><input type="number" className={inputCls} placeholder="0" value={form.droitEntreeMax} onChange={e => setField('droitEntreeMax', e.target.value)} /></div>
                  </div>
                </div>
              )}

              {activeFormTab === 'technique' && (
                <div className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div><label className={labelCls}>Hauteur min (m)</label><input type="number" step="0.1" className={inputCls} placeholder="3.0" value={form.hauteurMin} onChange={e => setField('hauteurMin', e.target.value)} /></div>
                    <div><label className={labelCls}>Puissance min (kVA)</label><input type="number" className={inputCls} placeholder="0" value={form.puissanceMin} onChange={e => setField('puissanceMin', e.target.value)} /></div>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    {[
                      { key: 'extraction', label: 'Extraction requise' },
                      { key: 'parking', label: 'Parking requis' },
                      { key: 'livraison', label: 'Accès livraison' },
                      { key: 'accessibilite', label: 'Accessibilité PMR' },
                      { key: 'terrasse', label: 'Terrasse souhaitée' },
                      { key: 'travauxPrisEnCharge', label: 'Travaux pris en charge' },
                    ].map(({ key, label }) => (
                      <label key={key} className="flex items-center gap-2 cursor-pointer p-3 rounded-lg border border-slate-200 hover:bg-slate-50 transition-colors">
                        <input type="checkbox" className="w-4 h-4 rounded border-slate-300 text-primary-700" checked={form[key as keyof EnseigneFormData] as boolean} onChange={e => setField(key as keyof EnseigneFormData, e.target.checked)} />
                        <span className="text-sm font-500 text-slate-700">{label}</span>
                      </label>
                    ))}
                  </div>
                </div>
              )}

              {activeFormTab === 'timing' && (
                <div className="space-y-4">
                  <div>
                    <label className={labelCls}>Urgence</label>
                    <select className={inputCls} value={form.urgence} onChange={e => setField('urgence', e.target.value)}>
                      {['Immédiate', 'Haute', 'Normale', 'Basse'].map(u => <option key={u}>{u}</option>)}
                    </select>
                  </div>
                  <div><label className={labelCls}>Horizon d'ouverture</label><input type="text" className={inputCls} placeholder="Ex: T3 2026, Fin 2026..." value={form.horizonOuverture} onChange={e => setField('horizonOuverture', e.target.value)} /></div>
                  <div><label className={labelCls}>Nb ouvertures prévues</label><input type="number" className={inputCls} placeholder="1" value={form.nbOuverturesPrevues} onChange={e => setField('nbOuverturesPrevues', e.target.value)} /></div>
                </div>
              )}
            </div>

            {/* Modal footer */}
            <div className="flex items-center justify-between p-6 border-t border-slate-200">
              <button onClick={() => setShowModal(false)} className="btn-secondary">Annuler</button>
              <div className="flex items-center gap-2">
                {FORM_TABS.findIndex(t => t.id === activeFormTab) < FORM_TABS.length - 1 && (
                  <button
                    onClick={() => {
                      const idx = FORM_TABS.findIndex(t => t.id === activeFormTab);
                      setActiveFormTab(FORM_TABS[idx + 1].id);
                    }}
                    className="btn-secondary"
                  >
                    Suivant →
                  </button>
                )}
                <button onClick={handleSave} disabled={saving} className="btn-primary disabled:opacity-60">
                  {saving ? (
                    <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                    </svg>
                  ) : (
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                  )}
                  {editingId ? 'Sauvegarder' : 'Créer l\'enseigne'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
